/**
 * `formatBytes` — locale-aware human-readable byte count formatter.
 *
 * Used by the wiki file-embed NodeView's chip label. Centralized here
 * rather than inlined at the call site because (a) the locale-aware
 * decimal separator threading is non-trivial — Spanish renders
 * `1,2 MB` where English renders `1.2 MB`; and (b) the file-upload
 * modal's size-cap copy ("File must be under 100 MB") shares the same
 * scale so the user sees consistent units between the cap copy and the
 * post-upload chip.
 *
 * Conventions:
 *   - Decimal SI scale (1 KB = 1000 bytes, not 1024). Matches macOS
 *     Finder + Notion + most user-facing surfaces; binary IEC (KiB /
 *     MiB) is reserved for dev tools. The cap copy talks in MB; the
 *     chip displays in MB; consistency wins over precision.
 *   - One decimal place for KB and up; whole bytes below 1 KB.
 *   - Unit label is NOT translated — `KB` / `MB` / `GB` / `TB` are
 *     SI symbols and read the same across our supported locales. The
 *     separator between number and unit is a NON-BREAKING SPACE so
 *     line-wrap can't split "1.2" from "MB" on a narrow chip.
 *
 * Locale routing via `Intl.NumberFormat(locale, ...)`:
 *   - Caller threads `i18n.language` from `useTranslation()`. The
 *     ICU-singleton pitfall applies — never default to `undefined`.
 *     Pure helper modules accept `locale` as a parameter rather than
 *     reading from a module-level format instance.
 *
 * Edge cases:
 *   - Negative / NaN input → returns "0 B" (silently clamps; defensive
 *     for unexpected BE shapes).
 *   - 0 bytes → returns "0 B".
 *   - Inputs ≥ 1 PB clamp at TB unit; the wiki cap is 100 MB so this
 *     is purely defensive.
 */

const SCALE = ['B', 'KB', 'MB', 'GB', 'TB'] as const
const NBSP = ' ' // U+00A0 NO-BREAK SPACE

export function formatBytes(bytes: number, locale: string): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    // Even the "0 B" sentinel goes through Intl so the digit shapes
    // localize (Arabic-Indic numerals in `ar` etc.). One-decimal-max
    // formatter for whole bytes is overkill but it keeps the format
    // chain uniform.
    return `${new Intl.NumberFormat(locale).format(0)}${NBSP}B`
  }

  // Pick the scale unit. log1000 keeps the math clean for the SI scale.
  let unitIdx = Math.floor(Math.log(bytes) / Math.log(1000))
  if (unitIdx < 0) unitIdx = 0
  if (unitIdx >= SCALE.length) unitIdx = SCALE.length - 1
  const value = bytes / 1000 ** unitIdx

  // Bytes: whole number. KB and up: one decimal place. The
  // `maximumFractionDigits: 1` matches Notion + GitHub.
  const fractionDigits = unitIdx === 0 ? 0 : 1
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  }).format(value)

  return `${formatted}${NBSP}${SCALE[unitIdx]}`
}
